// 개발요청 — 추가 요청(2판 V3): 검토자(AX) «추가 요청» 보내기·거두기 · 요청자 «답하기»(글 + 캡처·파일) · 추가 요청 기록.
// 🔴 단추는 서버 값으로만 — 보내기 = item.can.ask · 답하기 = item.can.answer_ask. «추가 요청 거두기»는 me.is_ax 이고
//    열린 추가 요청이 있을 때(서버 cancel 이 require_ax).
// 🔴 첨부 목록의 주인은 상세다 — ask_id 로 묶은 첨부(filesByAsk)·살아 있는 전체 수(totalCount)·썸네일(thumbs)을 받는다.
//    여기서 첨부 목록을 따로 부르지 않는다. 올리기·지우기가 성공할 때마다 onFilesChanged()(상세가 다시 받는다).
// 🔴 답하기 창이 열렸는지(answerOpen)는 상세가 갖는다 — «할 일» 상자의 «답하기»와 기록의 «답하기»가 같은 창을 연다.
//    쓰던 글·고른 파일은 여기 남는다(«요청 내용 고치기»로 고치기 창에 다녀와 다시 «답하기»를 누르면 그대로).
// 🔴 «답 보내기» = ✕ 한 첨부 지우기 → 고른 파일을 field=ask_answer·ask_id 로 하나씩 올리기 → answerAsk.
//    하나라도 실패하면 창을 닫지 않는다 — 올라간 것은 기존 칩 · 실패한 것만 남고, 다시 «답 보내기»는 남은 것만 올린다.
// 🔴 글은 글자로만 그린다(white-space: pre-wrap). 새 요소에 role="status" 를 쓰지 않는다(그것은 «불러오는 중…» 몫이다).
import { useEffect, useRef, useState } from 'react';
import { devreqAPI } from '../../api/client';
import { confirmDialog } from '../../components/AppDialog';
import { COLORS, RADIUS, btn, input as inputStyle } from '../../utils/uiStyles';
import { fmtKstDateTime } from '../../utils/kstTime';
import DevRequestAttachField, { FieldFiles, dropHasFiles } from './DevRequestAttachField';
import { AI_REVIEWER, ASKED_BY_AI, ASKED_BY_REVIEWER, errorText, text } from './devreqLabels';

const ASK_MAX = 1000;      // = routers/devreq.py _ASK_MAX
const ANSWER_MAX = 2000;   // = routers/devreq.py _ASK_ANSWER_MAX
const ASK_PLACEHOLDER = '예: 지금 쓰는 엑셀 양식 캡처를 올려 주세요';
const ANSWER_PLACEHOLDER = '요청받은 내용을 적거나 캡처를 붙여 주세요';
const ASK_FAILED = '추가 요청을 보내지 못했습니다.';
const CANCEL_FAILED = '추가 요청을 거두지 못했습니다.';
const ANSWER_FAILED = '답을 보내지 못했습니다.';
const UPLOAD_FAILED = '파일을 올리지 못했습니다.';
const DELETE_FAILED = '파일을 지우지 못했습니다.';
const CANCEL_CONFIRM = '거두면 요청자는 더 답할 수 없어요.';
const DISCARD_CONFIRM = '쓰던 내용을 버리고 닫을까요?';

// 글자 수 «1,000» — 실행 환경의 로캘과 무관하게 같은 꼴.
const commas = (n) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
const uniq = (list) => [...new Set(list)];
const metaStyle = { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px', fontSize: '12px', color: COLORS.textSub };
const bodyStyle = { margin: '4px 0 0', fontSize: '13px', color: COLORS.text, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' };
const pill = (background, color) => ({
  display: 'inline-block', padding: '1px 7px', borderRadius: '999px', fontSize: '11px', fontWeight: 700, background, color, whiteSpace: 'nowrap',
});
const STATE_PILLS = {
  open: ['답 기다리는 중', pill(COLORS.warnSoft, COLORS.warn)],
  answered: ['답함', pill(COLORS.successSoft, COLORS.successText)],
  cancelled: ['거둠', pill('#f3f4f6', COLORS.textSub)],
};

// 기록 섹션의 자리(«할 일» 상자의 «추가 요청 기록 보기»가 여기로 스크롤한다).
export const asksSectionId = (requestId) => `devreq-asks-${requestId}`;
export const openAskOf = (item) => (Array.isArray(item?.asks) ? item.asks.find((a) => a.state === 'open') : null) || null;

// 작은 창 — 열 때 첫 입력에 초점(부모가 준다) · Esc·바깥 누르기는 onRequestClose(쓰던 것이 있으면 부모가 확인 창).
//   휴대폰 상세(전체 화면 · zIndex 50)보다 위. 창 밖에 놓은 파일로 브라우저가 탭을 옮기지 않게 막는다.
function AskDialog({ titleId, title, onRequestClose, children }) {
  return (
    <div
      role="presentation"
      onMouseDown={(event) => { if (event.target === event.currentTarget) onRequestClose(); }}
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => { if (dropHasFiles(event)) event.preventDefault(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px', background: 'rgba(17,24,39,.45)',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            onRequestClose();
          }
        }}
        style={{
          width: 'min(560px, 100%)', maxHeight: '90vh', overflowY: 'auto', boxSizing: 'border-box',
          padding: '20px', borderRadius: RADIUS.lg, background: COLORS.surface, boxShadow: '0 18px 48px rgba(17,24,39,.2)',
        }}
      >
        <h2 id={titleId} style={{ margin: '0 0 10px', color: COLORS.text, fontSize: '17px' }}>{title}</h2>
        {children}
      </div>
    </div>
  );
}

const alertStyle = {
  margin: '0 0 10px', padding: '8px 12px', borderRadius: '8px', background: COLORS.dangerSoft, color: COLORS.dangerText,
  fontSize: '13px', overflowWrap: 'anywhere',
};

// 상세 머리(진행 단계 막대 오른쪽)의 AX 단추 — 열린 추가 요청이 있으면 «답 기다리는 중 · 보낸 시각» + «추가 요청 거두기»,
//   없고 can.ask 면 «추가 요청»(작은 창 — 1~1,000자 → ask → onChanged).
export function DevRequestAskControls({ item, me, onChanged }) {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const busyRef = useRef(false);
  const aliveRef = useRef(true);
  const areaRef = useRef(null);

  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  useEffect(() => {
    if (open) areaRef.current?.focus();
  }, [open]);

  const openAsk = openAskOf(item);
  const isAx = me?.is_ax === true;
  const canAsk = item?.can?.ask === true;

  const run = async (call, fallback, after) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setError('');
    try {
      await call();
      if (!aliveRef.current) return;
      after?.();
      onChanged?.();
    } catch (err) {
      const message = await errorText(err, fallback);
      if (aliveRef.current) setError(message);
    } finally {
      busyRef.current = false;
      if (aliveRef.current) setBusy(false);
    }
  };

  const close = async () => {
    if (busyRef.current) return;
    if (body.trim() && !(await confirmDialog(DISCARD_CONFIRM))) return;
    setOpen(false);
    setBody('');
    setError('');
  };
  const send = () => {
    const value = body.trim();
    if (!value) return;
    run(() => devreqAPI.ask(item.id, value), ASK_FAILED, () => { setOpen(false); setBody(''); });
  };
  const withdrawAsk = async () => {
    if (busyRef.current || !openAsk || !(await confirmDialog(CANCEL_CONFIRM))) return;
    run(() => devreqAPI.cancelAsk(openAsk.id), CANCEL_FAILED);
  };

  if (!item) return null;
  if (isAx && openAsk) {
    return (
      <div style={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px' }}>
        <span style={{ fontSize: '12px', color: COLORS.textSub }}>{`답 기다리는 중 · ${fmtKstDateTime(openAsk.created_at) || '—'}`}</span>
        <button type="button" onClick={withdrawAsk} disabled={busy} style={btn('secondary', 'sm')}>추가 요청 거두기</button>
        {error && <span role="alert" style={{ fontSize: '12px', color: COLORS.dangerText, overflowWrap: 'anywhere' }}>{error}</span>}
      </div>
    );
  }
  if (!canAsk) return null;
  const canSend = body.trim().length >= 1 && !busy;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} style={btn('accent', 'sm')}>추가 요청</button>
      {open && (
        <AskDialog titleId={`devreq-ask-title-${item.id}`} title="요청자에게 추가로 받을 것" onRequestClose={close}>
          {error && <div role="alert" style={alertStyle}>{error}</div>}
          <textarea ref={areaRef} aria-label="추가로 받을 것" value={body} maxLength={ASK_MAX} rows={4}
            placeholder={ASK_PLACEHOLDER} onChange={(e) => setBody(e.target.value)}
            style={{ ...inputStyle, resize: 'vertical', fontSize: '13px' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
            <span style={{ fontSize: '11px', color: COLORS.textSub }}>{`${commas(body.length)}/${commas(ASK_MAX)}`}</span>
            <button type="button" onClick={close} disabled={busy} style={{ ...btn('secondary', 'sm'), marginLeft: 'auto' }}>취소</button>
            <button type="button" onClick={send} disabled={!canSend} style={{ ...btn('primary', 'sm'), opacity: canSend ? 1 : 0.5 }}>
              {busy ? '보내는 중…' : '보내기'}
            </button>
          </div>
        </AskDialog>
      )}
    </>
  );
}

// 추가 요청 기록(메모 위) + 요청자 «답하기» 창.
export default function DevRequestAsks({
  item, filesByAsk, totalCount = 0, thumbs, onOpen, onDownload, onFilesChanged, openEdit, answerOpen = false, onAnswerOpen, onChanged,
}) {
  const [answer, setAnswer] = useState('');
  const [staged, setStaged] = useState([]);
  const [removals, setRemovals] = useState([]);       // ✕ 한(다음 «답 보내기»에 지울) 기존 첨부 id
  const [uploaded, setUploaded] = useState([]);       // 이 창이 올린 첨부 행(상세가 다시 받기 전까지 기존 칩으로 보인다)
  const [removedIds, setRemovedIds] = useState([]);   // 이 창이 지운 첨부 id(상세가 다시 받기 전까지 숨긴다)
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [listError, setListError] = useState('');
  const busyRef = useRef(false);
  const aliveRef = useRef(true);
  const areaRef = useRef(null);

  const asks = Array.isArray(item?.asks) ? item.asks : null;
  const openAsk = openAskOf(item);
  const openAskId = openAsk ? openAsk.id : null;
  const canAnswer = item?.can?.answer_ask === true && openAsk !== null;
  const canEdit = item?.can?.edit_part1 === true;
  const dialogShown = answerOpen && canAnswer;
  const byAsk = filesByAsk || {};
  const shownThumbs = thumbs || {};

  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  // 다른 추가 요청이 열리면(거두고 다시 보냄 등) 앞 추가 요청에 쓰던 첨부 상태는 버린다.
  useEffect(() => {
    const empty = (prev) => (prev.length ? [] : prev);
    setUploaded(empty);
    setRemovedIds(empty);
    setRemovals(empty);
  }, [openAskId]);

  useEffect(() => {
    if (dialogShown) areaRef.current?.focus();
  }, [dialogShown]);

  // 이 창의 기존 칩 — 상세가 아는 그 추가 요청의 첨부 + 이 창이 올린 것 − 이 창이 지운 것 − 지울 예정.
  const serverMine = openAsk ? (byAsk[openAsk.id] || []) : [];
  const serverIds = new Set(serverMine.map((f) => f.id));
  const mine = [...serverMine, ...uploaded.filter((f) => !serverIds.has(f.id))].filter((f) => !removedIds.includes(f.id));
  const chips = mine.filter((f) => !removals.includes(f.id));
  // 상한 — 이 창 밖의 첨부 수 = 상세가 아는 전체 − 상세가 아는 이 추가 요청의 첨부(이 창 몫은 부품이 센다 · 두 번 세지 않는다).
  const otherCount = Math.max(0, (Number(totalCount) || 0) - serverMine.length);
  const canSend = canAnswer && !busy && (answer.trim() !== '' || chips.length + staged.length > 0);

  const sendAnswer = async () => {
    if (!canSend || busyRef.current) return;
    const ask = openAsk;
    busyRef.current = true;
    setBusy(true);
    setError('');
    try {
      const messages = [];
      // ① ✕ 한 기존 첨부 지우기.
      const keep = [];
      for (const id of removals) {
        if (!aliveRef.current) return;
        try {
          await devreqAPI.removeFile(id);
          setRemovedIds((prev) => [...prev, id]);
          onFilesChanged?.();
        } catch (err) {
          keep.push(id);
          messages.push(await errorText(err, DELETE_FAILED));
        }
      }
      // ② 고른 파일을 하나씩 — 성공할 때마다 상세에 알린다(상한 계산이 틀리지 않게).
      const left = [];
      let failedUploads = 0;
      for (const file of staged) {
        if (!aliveRef.current) return;
        const formData = new FormData();
        formData.append('file', file);
        formData.append('field', 'ask_answer');
        formData.append('ask_id', String(ask.id));
        try {
          const r = await devreqAPI.upload(item.id, formData);
          const row = r?.data;
          if (row && row.id !== undefined && row.id !== null) {
            setUploaded((prev) => [...prev, { ...row, field: row.field ?? 'ask_answer', ask_id: row.ask_id ?? ask.id }]);
          }
          onFilesChanged?.();
        } catch (err) {
          left.push(file);
          failedUploads += 1;
          messages.push(await errorText(err, UPLOAD_FAILED));
        }
      }
      if (!aliveRef.current) return;
      setRemovals(keep);
      setStaged(left);
      if (messages.length > 0) {
        const parts = [];
        if (failedUploads > 0) parts.push(`첨부 ${failedUploads}개를 올리지 못했습니다.`);
        if (keep.length > 0) parts.push(`첨부 ${keep.length}개를 지우지 못했습니다.`);
        setError(`답은 아직 보내지 않았습니다. ${parts.join(' ')} ${uniq(messages).join(' · ')}`);
        return;
      }
      // ③ 답 — 실패하면 창 그대로(올라간 첨부는 기존 칩으로 남는다).
      try {
        await devreqAPI.answerAsk(ask.id, answer.trim());
      } catch (err) {
        const message = await errorText(err, ANSWER_FAILED);
        if (aliveRef.current) setError(message);
        return;
      }
      if (!aliveRef.current) return;
      setAnswer('');
      setStaged([]);
      setRemovals([]);
      setUploaded([]);
      setRemovedIds([]);
      onAnswerOpen?.(false);
      onChanged?.();
    } finally {
      busyRef.current = false;
      if (aliveRef.current) setBusy(false);
    }
  };

  const closeAnswer = async () => {
    if (busyRef.current) return;
    if ((answer.trim() || staged.length > 0) && !(await confirmDialog(DISCARD_CONFIRM))) return;
    setAnswer('');
    setStaged([]);
    setRemovals([]);
    setError('');
    onAnswerOpen?.(false);
  };
  // 쓰던 글·고른 파일은 남긴 채 창만 닫고 고치기 창으로(다시 «답하기»를 누르면 그대로).
  const goEdit = () => {
    if (busyRef.current) return;
    onAnswerOpen?.(false);
    openEdit?.();
  };

  // 거둔 추가 요청에 남은 첨부 지우기(can_delete 인 것만 ✕).
  const removeListed = async (file) => {
    if (!(await confirmDialog(`${file.name} 파일을 지울까요?`))) return;
    setListError('');
    try {
      await devreqAPI.removeFile(file.id);
      if (aliveRef.current) onFilesChanged?.();
    } catch (err) {
      const message = await errorText(err, DELETE_FAILED);
      if (aliveRef.current) setListError(message);
    }
  };

  return (
    <>
      {asks && asks.length > 0 && (
        <section id={asksSectionId(item.id)} aria-label="추가 요청"
          style={{ border: `1px solid ${COLORS.border}`, borderRadius: '10px', padding: '10px 14px', marginBottom: '12px', background: COLORS.surface }}>
          <h3 style={{ margin: '0 0 6px', fontSize: '13px', fontWeight: 700 }}>{`추가 요청 (${asks.length})`}</h3>
          <ul aria-label="추가 요청 기록" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {asks.map((ask) => {
              const [stateText, stateStyle] = STATE_PILLS[ask.state] || [ask.state, pill('#f3f4f6', COLORS.textSub)];
              const files = byAsk[ask.id] || [];
              return (
                <li key={ask.id} data-testid="devreq-ask-row" style={{ padding: '8px 0', borderTop: `1px solid ${COLORS.borderSoft}` }}>
                  <div style={metaStyle}>
                    {/* AI 가 보낸 추가 요청(서버 by_ai)은 시스템 계정 이름 대신 «AI 검토» + «AI» 알약 */}
                    {ask.by_ai === true ? (
                      <>
                        <span style={{ fontWeight: 700, color: COLORS.text }}>{AI_REVIEWER}</span>
                        <span style={pill(COLORS.infoSoft, COLORS.info)}>AI</span>
                      </>
                    ) : (
                      <span style={{ fontWeight: 700, color: COLORS.text }}>{text(ask.asked_by_name)}</span>
                    )}
                    <span>{fmtKstDateTime(ask.created_at) || '—'}</span>
                    {stateText && <span style={stateStyle}>{stateText}</span>}
                  </div>
                  <p style={bodyStyle}>{ask.body}</p>
                  {ask.state === 'answered' && (
                    <div style={{ marginTop: '6px', paddingLeft: '10px', borderLeft: `3px solid ${COLORS.successSoft}` }}>
                      <div style={metaStyle}>
                        <span style={{ fontWeight: 700, color: COLORS.text }}>{text(ask.answered_by_name)}</span>
                        <span>{fmtKstDateTime(ask.answered_at) || '—'}</span>
                      </div>
                      {ask.answer && <p style={bodyStyle}>{ask.answer}</p>}
                      <FieldFiles label="답" files={files} thumbs={shownThumbs} onOpen={onOpen} onDownload={onDownload} />
                    </div>
                  )}
                  {ask.state === 'cancelled' && (
                    <FieldFiles label="거둔 추가 요청" files={files} thumbs={shownThumbs} onOpen={onOpen} onDownload={onDownload} onRemove={removeListed} />
                  )}
                  {ask.state === 'open' && canAnswer && (
                    <button type="button" onClick={() => onAnswerOpen?.(true)} style={{ ...btn('primary', 'sm'), marginTop: '6px' }}>답하기</button>
                  )}
                </li>
              );
            })}
          </ul>
          {listError && <p role="alert" style={{ margin: '6px 0 0', fontSize: '12px', color: COLORS.dangerText }}>{listError}</p>}
        </section>
      )}

      {dialogShown && (
        <AskDialog titleId={`devreq-answer-title-${item.id}`} title="추가 요청에 답하기" onRequestClose={closeAnswer}>
          <div style={{ marginBottom: '12px', padding: '10px 12px', borderRadius: RADIUS.md, background: COLORS.warnSoft }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: COLORS.warn }}>{openAsk.by_ai === true ? ASKED_BY_AI : ASKED_BY_REVIEWER}</div>
            <p style={bodyStyle}>{openAsk.body}</p>
          </div>
          {error && <div role="alert" style={alertStyle}>{error}</div>}
          <DevRequestAttachField
            label="답"
            files={chips}
            staged={staged}
            onStage={(list) => setStaged((prev) => [...prev, ...list])}
            onUnstage={(index) => setStaged((prev) => prev.filter((_, i) => i !== index))}
            onRemoveExisting={(id) => setRemovals((prev) => (prev.includes(id) ? prev : [...prev, id]))}
            otherCount={otherCount}
            textareaRef={areaRef}
            disabled={busy}
            thumbs={shownThumbs}
            style={{ marginBottom: '12px' }}
          >
            <label htmlFor={`devreq-answer-${item.id}`} style={{ display: 'block', marginBottom: '6px', color: COLORS.text, fontSize: '13px', fontWeight: 700 }}>답</label>
            <textarea id={`devreq-answer-${item.id}`} ref={areaRef} aria-label="답" value={answer} maxLength={ANSWER_MAX} rows={4}
              placeholder={ANSWER_PLACEHOLDER} onChange={(e) => setAnswer(e.target.value)}
              style={{ ...inputStyle, resize: 'vertical', fontSize: '13px' }} />
            <div style={{ marginTop: '4px', textAlign: 'right', fontSize: '11px', color: COLORS.textSub }}>
              {`${commas(answer.length)}/${commas(ANSWER_MAX)}`}
            </div>
          </DevRequestAttachField>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
            {canEdit && (
              <button type="button" onClick={goEdit} disabled={busy} style={btn('secondary', 'sm')}>요청 내용 고치기</button>
            )}
            <button type="button" onClick={closeAnswer} disabled={busy} style={{ ...btn('secondary', 'sm'), marginLeft: 'auto' }}>취소</button>
            <button type="button" onClick={sendAnswer} disabled={!canSend} style={{ ...btn('primary', 'sm'), opacity: canSend ? 1 : 0.5 }}>
              {busy ? '보내는 중…' : '답 보내기'}
            </button>
          </div>
        </AskDialog>
      )}
    </>
  );
}
